import React, { useState, useEffect } from "react";
import axios from "axios";
import { CSVLink } from "react-csv";
import PageTitle from "../../components/common/PageTitle";
import { Container, Row, Card, Button } from "shards-react";

const csvHeaders = {
  2024: [
    { label: "Student ID", key: "sId" },
    { label: "Email", key: "email_id" },
    { label: "First name", key: "first_name" },
    { label: "Middle name", key: "middle_name" },
    { label: "Last name", key: "last_name" },
    { label: "Gender", key: "gender" },
    { label: "Class", key: "class" },
    { label: "Institution", key: "school" },
    { label: "Institution type", key: "school_type" },
    { label: "City", key: "city" },
    { label: "State", key: "state" },
    { label: "Country", key: "country" },
    { label: "Category", key: "iaif24_category" },
    { label: "Sub category", key: "iaif24_subCategory" },
    {
      label: "Eligible for special award",
      key: "iaif24_identifyNeurodivergent",
    },
    {
      label: "Special award category",
      key: "iaif24_focusedAiForAccessibility",
    },
    { label: "Special award sub category", key: "iaif24_aimingChallenge" },
    { label: "Aspirational district", key: "iaif24_source" },
    { label: "UN SDG", key: "sdg" },
    { label: "Ethical concern", key: "ethicalConcern" },
    { label: "Project PDF", key: "pdf" },
    { label: "Project video", key: "video" },
    { label: "Project type", key: "projectType" },
    { label: "Project members (Old)", key: "members" },
    { label: "Project member 1", key: "member1" },
    { label: "Project member 2", key: "member2" },
    { label: "Project member 3", key: "member3" },
    { label: "Referral code", key: "referralCode" },
    { label: "Source", key: "iaif24_source" },
    { label: "Submission status", key: "iaif24_status" },
    { label: "Submission date", key: "iaif24_submissionDate" },
  ],
  2025: [
    { label: "Student ID", key: "sId" },
    { label: "Email", key: "email_id" },
    { label: "First name", key: "first_name" },
    { label: "Middle name", key: "middle_name" },
    { label: "Last name", key: "last_name" },
    { label: "Gender", key: "gender" },
    { label: "Class", key: "class" },
    { label: "Institution", key: "school" },
    { label: "Institution type", key: "school_type" },
    { label: "City", key: "city" },
    { label: "State", key: "state" },
    { label: "Country", key: "country" },
    { label: "Project Synopsis", key: "synopsis" },
    { label: "Category", key: "iaif25_category" },
    {
      label: "Is your project focused on AI for accessibility?",
      key: "iaif25_focusedAiForAccessibility",
    },
    {
      label:
        "Do you identify as neurodivergent (e.g., autism, ADHD, dyslexia, etc.)?",
      key: "iaif25_identifyNeurodivergent",
    },
    {
      label:
        "If yes, what specific challenges or issues of accessibility is your project aiming to address?",
      key: "iaif25_aimingChallenge",
    },
    { label: "Sub Category", key: "iaif25_subCategory" },
    { label: "Business Plan", key: "businessPlan" },
    { label: "Ethical concern", key: "ethicalConcern" },
    { label: "Project members", key: "members" },
    { label: "Project Type", key: "projectType" },
    { label: "UN SDG", key: "sdg" },
    { label: "Project PDF", key: "pdf" },
    { label: "Project Video", key: "video" },
    { label: "Project YouTube Link", key: "videoURL" },
    { label: "Submission Status", key: "iaif25_status" },
    { label: "Submission date", key: "iaif25_submissionDate" },
  ],
};

const normalizeCell = (val) => {
  if (val === null || val === undefined) return "";

  let s;
  if (Array.isArray(val)) {
    s = val
      .map(function (v) {
        return v != null ? String(v) : "";
      })
      .join(" | ");
  } else if (typeof val === "object") {
    try {
      s = JSON.stringify(val);
    } catch (e) {
      s = String(val);
    }
  } else {
    s = String(val);
  }

  s = s
    .replace(/\r?\n/g, " ")
    .replace(/\t/g, " ")
    .replace(/\u2028|\u2029/g, " ");

  s = s.replace(/\\"/g, '"').replace(/\\'/g, "'");
  s = s.replace(/"/g, '""');

  return s.trim();
};

const filterDataByHeaders = (data, headers) => {
  const keys = headers.map(function (h) {
    return h.key;
  });
  return (data || []).map(function (row) {
    const filtered = {};
    keys.forEach(function (key) {
      const value =
        row && Object.prototype.hasOwnProperty.call(row, key) ? row[key] : "";
      filtered[key] = normalizeCell(value);
    });
    return filtered;
  });
};

const CollapsibleRow = ({ year }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [listLoaded, setListLoaded] = useState(false);

  const fetchData = async (yearToFetch) => {
    setListLoaded(false);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/impact-festival/participants?year=${yearToFetch}`,
        { headers: { "x-access-token": localStorage.getItem("jwtToken") } }
      );
      setData((response && response.data && response.data.data) || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    } finally {
      setListLoaded(true);
    }
  };

  useEffect(() => {
    if (open) fetchData(year);
  }, [open, year]);

  return (
    <Card
      onClick={() => setOpen(!open)}
      className="p-3 shadow-sm my-3 rounded-lg"
    >
      <div className="d-flex justify-content-between align-items-center">
        <h5 className="m-0 font-weight-bold text-dark">
          Impact Festival {year}
        </h5>
        <span
          className={`font-weight-bold ${
            open ? "text-danger" : "text-primary"
          }`}
        >
          {open ? "▲" : "▼"}
        </span>
      </div>

      {open && (
        <div className="mt-3">
          {listLoaded ? (
            <CSVLink
              data={filterDataByHeaders(data, csvHeaders[String(year)])}
              headers={csvHeaders[String(year)]}
              filename={`Impact-Festival-${year}.csv`}
              enclosingCharacter={`"`}
              separator=","
            >
              <Button
                size="sm"
                theme="primary"
                onClick={(e) => e.stopPropagation()}
              >
                Download CSV
              </Button>
            </CSVLink>
          ) : (
            <Button size="sm" disabled onClick={(e) => e.stopPropagation()}>
              Loading...
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

const ImpactFestivalDashboard = () => {
  const years = [2024, 2025];

  return (
    <Container fluid className="main-content-container px-4">
      <Row noGutters className="page-header py-4">
        <PageTitle
          sm="4"
          title="India AI Impact Festivals List"
          subtitle=""
          className="text-sm-left"
        />
      </Row>
      {years.map((year) => (
        <CollapsibleRow key={year} year={year} />
      ))}
    </Container>
  );
};

export default ImpactFestivalDashboard;
